import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useTabParam from '../../hooks/useTabParam';
import useDetailBreadcrumbs from '../../hooks/useDetailBreadcrumbs';
import { useCapturePageContext } from '../../capture/CaptureContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import {
  getAccount,
  updateAccount,
  deleteAccount,
  getStages,
  getLeadSources,
  getUsers,
  getOwnershipRoleTypes,
  getRepFirms,
  createActivity,
  createTask,
  createAccountAddress,
  updateAccountAddress,
  deleteAccountAddress,
  createContact,
  updateContact,
  deleteContact,
  createOpportunity,
  getOpportunityStages,
  getProducts,
  createQuote,
  getAccounts,
  setAccountParent,
  optional,
} from '../../api';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Plus,
  MessageSquare,
} from 'lucide-react';

import AccountOverviewTab from './AccountOverviewTab';
import AccountWeeklySummary from '../../components/AccountWeeklySummary';
import AccountContactsTab from './AccountContactsTab';
import AccountInfluenceMapTab from './AccountInfluenceMapTab';
import AccountHierarchyMapTab from './AccountHierarchyMapTab';
import AccountOrgChartTab from './AccountOrgChartTab';
import AccountOpportunitiesTab from './AccountOpportunitiesTab';
import AccountActivitiesTab from './AccountActivitiesTab';
import AccountTasksTab from './AccountTasksTab';
import KeyStar from '../../components/KeyStar';
import { AskAIButton } from '../../components/ai';
import { updateAccountGoal } from '../../api';
import AccountQuotesTab from './AccountQuotesTab';
import AccountOrdersTab from './AccountOrdersTab';
import {
  EditAccountModal,
  DeleteAccountModal,
  LogActivityModal,
  CreateTaskModal,
  AddressModal,
  ContactModal,
  DeleteContactConfirm,
  DeleteAddressConfirm,
  CreateOpportunityModal,
  CreateQuoteModal,
} from './AccountModals';

import './AccountDetail.css';

const ACCOUNT_TABS = ['overview', 'contacts', 'influence-map', 'account-map', 'org-chart', 'opportunities', 'activities', 'tasks', 'quotes', 'orders'];

function AccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPermission, canDo, user, isModuleVisible } = useAuth();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');

  // Reference data
  const [stages, setStages] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [users, setUsers] = useState([]);
  const [ownershipRoleTypes, setOwnershipRoleTypes] = useState([]);
  const [repFirms, setRepFirms] = useState([]);
  const [opportunityStages, setOpportunityStages] = useState([]);
  const [products, setProducts] = useState([]);
  // All accounts — powers the Overview "Parent account" picker.
  const [allAccounts, setAllAccounts] = useState([]);

  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteContacts, setDeleteContacts] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [showDeleteAddressConfirm, setShowDeleteAddressConfirm] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showDeleteContactConfirm, setShowDeleteContactConfirm] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [showOpportunityModal, setShowOpportunityModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);

  // Inline edit mode
  const [isEditingAccount, setIsEditingAccount] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [industrySearchText, setIndustrySearchText] = useState('');

  const INDUSTRIES = [
    'Aerospace & Defense', 'Agriculture', 'Automotive', 'Banking & Finance',
    'Biotechnology', 'Chemicals', 'Construction', 'Consumer Electronics',
    'Data Centers', 'Education', 'Energy', 'Entertainment',
    'Food & Beverage', 'Government', 'Healthcare', 'Hospitality',
    'Insurance', 'Logistics & Transportation', 'Manufacturing', 'Media',
    'Mining', 'Networking & Telecom', 'Oil & Gas', 'Pharmaceuticals',
    'Real Estate', 'Retail', 'Semiconductors', 'Software & IT',
    'Technology', 'Utilities',
  ];
  const industryItems = INDUSTRIES.concat(
    account?.industry && !INDUSTRIES.includes(account.industry) ? [account.industry] : []
  ).sort().map(name => ({ id: name, name }));

  // Form data
  const [formData, setFormData] = useState({});
  const [activityForm, setActivityForm] = useState({ type: 'Call', description: '', contact_id: '' });
  const [taskForm, setTaskForm] = useState({ title: '', due_date: '', assigned_to: '', contact_id: '' });
  const [addressForm, setAddressForm] = useState({
    address_type: 'Shipping',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    is_headquarters: false,
    is_default_shipping: false,
  });
  const [contactForm, setContactForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    job_title: '',
    linkedin_url: '',
    active: true,
    market_to: true,
    lead_source_id: '',
    rep_firm_id: '',
    owner_id: user?.id || '',
    notes: '',
    influence_role: '',
    social_style: '',
    relationship_status: '',
    disposition: '',
  });
  const [opportunityForm, setOpportunityForm] = useState({
    contact_id: '',
    owner_id: '',
    stage_id: '',
    expected_close_date: '',
    rep_firm_id: '',
    notes: '',
    line_items: [],
  });
  const [quoteForm, setQuoteForm] = useState({
    contact_id: '',
    ship_to_address_id: '',
    estimated_ship_date: '',
    notes: '',
    line_items: [],
  });

  // Tabs — in the URL (?tab=…) so refresh, Back and shared links keep them.
  const [activeTab, setActiveTab] = useTabParam('overview', ACCOUNT_TABS);
  useDetailBreadcrumbs(account ? [{ label: 'Accounts', path: '/accounts' }, { label: account.name }] : null);
  useCapturePageContext(account ? { account_id: account.id, account_name: account.name } : null);

  // Sort state for tab tables
  const [contactsSort, setContactsSort] = useState({ field: 'name', direction: 'asc' });
  const [opportunitiesSort, setOpportunitiesSort] = useState({ field: 'name', direction: 'asc' });
  const [tasksSort, setTasksSort] = useState({ field: 'due_date', direction: 'asc' });
  const [quotesSort, setQuotesSort] = useState({ field: 'created_at', direction: 'desc' });
  const [ordersSort, setOrdersSort] = useState({ field: 'order_date', direction: 'desc' });

  // Generic sort function
  const sortItems = useCallback((items, sortField, sortDirection) => {
    if (!items) return [];
    return [...items].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // Handle dates
      if (sortField.includes('date') || sortField === 'created_at') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, []);

  // Sorted data for each tab
  const sortedContacts = useMemo(() =>
    sortItems(account?.contacts, contactsSort.field, contactsSort.direction),
    [account?.contacts, contactsSort.field, contactsSort.direction, sortItems]
  );

  const sortedOpportunities = useMemo(() =>
    sortItems(account?.opportunities, opportunitiesSort.field, opportunitiesSort.direction),
    [account?.opportunities, opportunitiesSort.field, opportunitiesSort.direction, sortItems]
  );

  const sortedTasks = useMemo(() =>
    sortItems(account?.tasks, tasksSort.field, tasksSort.direction),
    [account?.tasks, tasksSort.field, tasksSort.direction, sortItems]
  );

  const sortedQuotes = useMemo(() =>
    sortItems(account?.quotes, quotesSort.field, quotesSort.direction),
    [account?.quotes, quotesSort.field, quotesSort.direction, sortItems]
  );

  const sortedOrders = useMemo(() =>
    sortItems(account?.orders, ordersSort.field, ordersSort.direction),
    [account?.orders, ordersSort.field, ordersSort.direction, sortItems]
  );

  // Sort handlers
  const handleContactsSort = (field, direction) => setContactsSort({ field, direction: field === 'is_key_contact' && contactsSort.field !== 'is_key_contact' ? 'desc' : direction });
  const handleOpportunitiesSort = (field, direction) => setOpportunitiesSort({ field, direction });
  const handleTasksSort = (field, direction) => setTasksSort({ field, direction });
  const handleQuotesSort = (field, direction) => setQuotesSort({ field, direction });
  const handleOrdersSort = (field, direction) => setOrdersSort({ field, direction });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      // Only the account itself is required; the reference lists may be
      // module-restricted for this user and must not block the page.
      const [accountRes, stagesRes, sourcesRes, usersRes, ownerTypesRes, repFirmsRes, oppStagesRes, productsRes] = await Promise.all([
        getAccount(id),
        optional(getStages()),
        optional(getLeadSources()),
        optional(getUsers()),
        optional(getOwnershipRoleTypes()),
        optional(getRepFirms()),
        optional(getOpportunityStages()),
        optional(getProducts()),
      ]);
      setAccount(accountRes.data);
      setFormData({
        ...accountRes.data,
        owners: accountRes.data.owners || [],
      });
      setStages(stagesRes.data);
      setLeadSources(sourcesRes.data);
      setUsers(usersRes.data.filter(u => u.active));
      setOwnershipRoleTypes(ownerTypesRes.data);
      setRepFirms(repFirmsRes.data);
      setOpportunityStages(oppStagesRes.data);
      setProducts(productsRes.data.filter(p => p.active));
    } catch (err) {
      setError('Failed to load account');
    } finally {
      setLoading(false);
    }
  };

  // Load the account list for the Parent-account picker (non-blocking).
  useEffect(() => {
    optional(getAccounts()).then(res => {
      const list = Array.isArray(res?.data) ? res.data : (res?.data?.data || []);
      setAllAccounts(list.map(a => ({ id: a.id, name: a.name })));
    });
  }, []);

  // Set (or clear) this account's parent via the validated hierarchy endpoint,
  // then refresh so the Overview parent/subsidiaries update.
  const handleSetParent = async (parentId) => {
    try {
      setModalError('');
      await setAccountParent(id, parentId === '' || parentId == null ? null : parseInt(parentId, 10));
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update parent account');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateAccount(id, formData);
      setShowEditModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update account');
    }
  };

  // Inline edit mode helpers
  const startEditingAccount = () => {
    if (!canDo('accounts', 'edit')) return;
    setFormData({
      ...account,
      owners: account.owners?.map(o => ({ ...o })) || [],
    });
    setIndustrySearchText(account.industry || '');
    setIsEditingAccount(true);
  };

  const cancelEditingAccount = () => {
    setIsEditingAccount(false);
    setFormData({});
  };

  const saveEditingAccount = async () => {
    setEditSaving(true);
    try {
      await updateAccount(id, formData);
      setIsEditingAccount(false);
      setFormData({});
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update account');
    } finally {
      setEditSaving(false);
    }
  };

  const updateAccountField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // One field, saved on its own: the whole record goes with it, as the
  // update route expects, with only that field changed.
  const saveAccountField = async (field, value) => {
    const body = { ...account, owners: account.owners?.map(o => ({ ...o })) || [], [field]: value };
    await updateAccount(id, body);
    await loadData();
  };

  const handleDelete = async () => {
    try {
      await deleteAccount(id, deleteContacts);
      navigate('/accounts');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete account');
    }
  };

  const handleLogActivity = async (e) => {
    e.preventDefault();
    try {
      await createActivity({
        ...activityForm,
        account_id: id,
      });
      setShowActivityModal(false);
      setModalError('');
      setActivityForm({ type: 'Call', description: '', contact_id: '' });
      loadData();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to log activity');
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await createTask({
        ...taskForm,
        account_id: id,
        assigned_to: taskForm.assigned_to || user.id,
      });
      setShowTaskModal(false);
      setModalError('');
      setTaskForm({ title: '', due_date: '', assigned_to: '', contact_id: '' });
      loadData();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to create action item');
    }
  };

  const handleAddAddress = () => {
    setEditingAddress(null);
    setAddressForm({
      address_type: 'Shipping',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      zip_code: '',
      country: '',
      is_headquarters: false,
      is_default_shipping: false,
    });
    setShowAddressModal(true);
  };

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setAddressForm({
      address_type: address.address_type || 'Shipping',
      address_line1: address.address_line1 || '',
      address_line2: address.address_line2 || '',
      city: address.city || '',
      state: address.state || '',
      zip_code: address.zip_code || '',
      country: address.country || '',
      is_headquarters: !!address.is_headquarters,
      is_default_shipping: !!address.is_default_shipping,
    });
    setShowAddressModal(true);
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    try {
      if (editingAddress) {
        await updateAccountAddress(id, editingAddress.id, addressForm);
      } else {
        await createAccountAddress(id, addressForm);
      }
      setShowAddressModal(false);
      setModalError('');
      loadData();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to save address');
    }
  };

  const handleDeleteAddress = (addressId) => {
    setAddressToDelete(addressId);
    setShowDeleteAddressConfirm(true);
  };

  const confirmDeleteAddress = async () => {
    if (!addressToDelete) return;
    try {
      await deleteAccountAddress(id, addressToDelete);
      setShowDeleteAddressConfirm(false);
      setAddressToDelete(null);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete address');
    }
  };

  // Contact creation/editing
  const handleOpenContactModal = (contact = null) => {
    if (contact) {
      // Editing existing contact
      setEditingContact(contact);
      // Parse name into first_name and last_name if needed
      const nameParts = (contact.name || '').trim().split(' ');
      setContactForm({
        account_id: contact.account_id || Number(id),
        first_name: contact.first_name || nameParts[0] || '',
        last_name: contact.last_name || nameParts.slice(1).join(' ') || '',
        email: contact.email || '',
        phone: contact.phone || '',
        job_title: contact.job_title || '',
        linkedin_url: contact.linkedin_url || '',
        active: contact.active !== undefined ? !!contact.active : true,
        market_to: contact.market_to !== undefined ? !!contact.market_to : true,
        lead_source_id: contact.lead_source_id || '',
        rep_firm_id: contact.rep_firm_id || '',
        owner_id: contact.owner_id || '',
        notes: contact.notes || '',
        influence_role: contact.influence_role || '',
        social_style: contact.social_style || '',
        relationship_status: contact.relationship_status || '',
        disposition: contact.disposition == null ? '' : contact.disposition,
      });
    } else {
      // Creating new contact: on this account unless the person picks another.
      setEditingContact(null);
      setContactForm({
        account_id: Number(id),
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        job_title: '',
        linkedin_url: '',
        active: true,
        market_to: true,
        lead_source_id: '',
        rep_firm_id: '',
        owner_id: user?.id || '',
        notes: '',
        influence_role: '',
        social_style: '',
        relationship_status: '',
        disposition: '',
      });
    }
    setShowContactModal(true);
  };

  const handleSaveContact = async (e) => {
    e.preventDefault();
    // The form carries the account; it is this one unless the person chose another.
    const accountId = Number(contactForm.account_id) || Number(id);
    const elsewhere = accountId !== Number(id);
    try {
      let savedId;
      if (editingContact) {
        await updateContact(editingContact.id, { ...contactForm, account_id: accountId });
        savedId = editingContact.id;
      } else {
        const res = await createContact({ ...contactForm, account_id: accountId });
        savedId = res.data && res.data.id;
      }
      if (elsewhere) {
        const target = allAccounts.find((a) => a.id === accountId);
        const who = `${contactForm.first_name || ''} ${contactForm.last_name || ''}`.trim() || 'the contact';
        toast.success(`${editingContact ? 'Moved' : 'Added'} ${who} to ${target ? target.name : 'the other account'}`, savedId ? { action: { label: 'Open', onClick: () => navigate(`/contacts/${savedId}`) } } : undefined);
      }
      setShowContactModal(false);
      setModalError('');
      setEditingContact(null);
      setContactForm({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        job_title: '',
        linkedin_url: '',
        active: true,
        market_to: true,
        lead_source_id: '',
        rep_firm_id: '',
        owner_id: user?.id || '',
        notes: '',
        influence_role: '',
        social_style: '',
        relationship_status: '',
        disposition: '',
      });
      loadData();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to save contact');
    }
  };

  const handleDeleteContact = async () => {
    if (!contactToDelete) return;
    try {
      await deleteContact(contactToDelete.id);
      setShowDeleteContactConfirm(false);
      setContactToDelete(null);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete contact');
    }
  };

  // Opportunity creation
  const handleCreateOpportunity = async (e) => {
    e.preventDefault();
    try {
      await createOpportunity({
        ...opportunityForm,
        account_id: id,
      });
      setShowOpportunityModal(false);
      setModalError('');
      setOpportunityForm({
        contact_id: '',
        owner_id: '',
        stage_id: '',
        expected_close_date: '',
        rep_firm_id: '',
        notes: '',
        line_items: [],
      });
      loadData();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to create opportunity');
    }
  };

  const addOpportunityLineItem = () => {
    setOpportunityForm(prev => ({
      ...prev,
      line_items: [...prev.line_items, { product_id: '', product_name: '', description: '', sku: '', quantity: 1, unit_price: 0 }],
    }));
  };

  const updateOpportunityLineItem = (index, field, value) => {
    setOpportunityForm(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => {
        if (i === index) {
          const updated = { ...item, [field]: value };
          if (field === 'product_id' && value) {
            const product = products.find(p => p.id === parseInt(value));
            if (product) {
              updated.product_name = product.name;
              updated.description = product.description || '';
              updated.sku = product.sku || '';
              updated.unit_price = product.price;
            }
          }
          return updated;
        }
        return item;
      }),
    }));
  };

  const removeOpportunityLineItem = (index) => {
    setOpportunityForm(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index),
    }));
  };

  // Quote creation
  const handleCreateQuote = async (e) => {
    e.preventDefault();
    try {
      await createQuote({
        ...quoteForm,
        account_id: id,
      });
      setShowQuoteModal(false);
      setModalError('');
      setQuoteForm({
        contact_id: '',
        ship_to_address_id: '',
        estimated_ship_date: '',
        notes: '',
        line_items: [],
      });
      loadData();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to create quote');
    }
  };

  const addQuoteLineItem = () => {
    setQuoteForm(prev => ({
      ...prev,
      line_items: [...prev.line_items, { product_id: '', product_name: '', sku: '', description: '', quantity: 1, unit_price: 0 }],
    }));
  };

  const updateQuoteLineItem = (index, field, value) => {
    setQuoteForm(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => {
        if (i === index) {
          const updated = { ...item, [field]: value };
          if (field === 'product_id' && value) {
            const product = products.find(p => p.id === parseInt(value));
            if (product) {
              updated.product_name = product.name;
              updated.sku = product.sku || '';
              updated.description = product.description || '';
              updated.unit_price = product.price;
            }
          }
          return updated;
        }
        return item;
      }),
    }));
  };

  const removeQuoteLineItem = (index) => {
    setQuoteForm(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index),
    }));
  };

  const calculateOpportunityTotal = () => {
    return opportunityForm.line_items.reduce((sum, item) => sum + (item.quantity || 0) * (item.unit_price || 0), 0);
  };

  const calculateQuoteTotal = () => {
    return quoteForm.line_items.reduce((sum, item) => sum + (item.quantity || 0) * (item.unit_price || 0), 0);
  };

  const addOwner = () => {
    setFormData(prev => ({
      ...prev,
      owners: [...prev.owners, { user_id: '', ownership_role_type_id: '', is_primary: false }],
    }));
  };

  const updateOwner = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      owners: prev.owners.map((o, i) => i === index ? { ...o, [field]: value } : o),
    }));
  };

  const removeOwner = (index) => {
    setFormData(prev => ({
      ...prev,
      owners: prev.owners.filter((_, i) => i !== index),
    }));
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!account) {
    return <div className="alert alert-error">Account not found</div>;
  }

  // The contact modal's account list always includes this account, whatever the lookup returned.
  const contactAccountOptions = allAccounts.some((a) => a.id === Number(id)) ? allAccounts : [{ id: Number(id), name: account.name }, ...allAccounts];

  return (
    <div className="account-detail">
      <div className="page-header">
        <div className="flex items-center gap-4">
          <button className="btn btn-icon" onClick={() => navigate('/accounts')}>
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {account.name}
              <KeyStar on={!!account.is_key_account} name={account.name} what="key account" size={20} onToggle={canDo('accounts', 'edit') ? async (on) => { await updateAccountGoal(id, { is_key_account: on }); await loadData(); } : undefined} />
            </h1>
            {account.stage_name && (
              <span className="badge badge-primary">{account.stage_name}</span>
            )}
          </div>
        </div>
        <div className="page-actions">
          <AskAIButton name={account.name} />
          {isModuleVisible('activities') && canDo('activities', 'create') && (
            <button className="btn btn-secondary" onClick={() => setShowActivityModal(true)}>
              <MessageSquare size={18} />
              Log Activity
            </button>
          )}
          {isModuleVisible('tasks') && canDo('tasks', 'create') && (
            <button className="btn btn-secondary" onClick={() => setShowTaskModal(true)}>
              <Plus size={18} />
              Add Action Item
            </button>
          )}
          {canDo('accounts', 'delete') && (
            <button className="btn btn-danger" onClick={() => setShowDeleteConfirm(true)}>
              <Trash2 size={18} />
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        {isModuleVisible('contacts') && (
          <button
            className={`tab ${activeTab === 'contacts' ? 'active' : ''}`}
            onClick={() => setActiveTab('contacts')}
          >
            Contacts ({account.contacts?.length || 0})
          </button>
        )}
        {isModuleVisible('contacts') && (
          <button
            className={`tab ${activeTab === 'influence-map' ? 'active' : ''}`}
            onClick={() => setActiveTab('influence-map')}
          >
            Influence Map
          </button>
        )}
        <button
          className={`tab ${activeTab === 'account-map' ? 'active' : ''}`}
          onClick={() => setActiveTab('account-map')}
        >
          Account Map
        </button>
        {isModuleVisible('contacts') && (
          <button
            className={`tab ${activeTab === 'org-chart' ? 'active' : ''}`}
            onClick={() => setActiveTab('org-chart')}
          >
            Org Chart
          </button>
        )}
        {isModuleVisible('opportunities') && (
          <button
            className={`tab ${activeTab === 'opportunities' ? 'active' : ''}`}
            onClick={() => setActiveTab('opportunities')}
          >
            Opportunities ({account.opportunities?.length || 0})
          </button>
        )}
        {isModuleVisible('activities') && (
          <button
            className={`tab ${activeTab === 'activities' ? 'active' : ''}`}
            onClick={() => setActiveTab('activities')}
          >
            Activities ({account.activities?.length || 0})
          </button>
        )}
        {isModuleVisible('tasks') && (
          <button
            className={`tab ${activeTab === 'tasks' ? 'active' : ''}`}
            onClick={() => setActiveTab('tasks')}
          >
            Action Items ({account.tasks?.length || 0})
          </button>
        )}
        {isModuleVisible('quotes') && (
          <button
            className={`tab ${activeTab === 'quotes' ? 'active' : ''}`}
            onClick={() => setActiveTab('quotes')}
          >
            Quotes ({account.quotes?.length || 0})
          </button>
        )}
        {isModuleVisible('orders') && (
          <button
            className={`tab ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            Orders ({account.orders?.length || 0})
          </button>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <AccountOverviewTab
          account={account}
          canDo={canDo}
          handleAddAddress={handleAddAddress}
          handleEditAddress={handleEditAddress}
          handleDeleteAddress={handleDeleteAddress}
          isEditing={isEditingAccount}
          formData={formData}
          updateField={updateAccountField}
          saveField={saveAccountField}
          startEditing={startEditingAccount}
          cancelEditing={cancelEditingAccount}
          saveEditing={saveEditingAccount}
          saving={editSaving}
          stages={stages}
          leadSources={leadSources}
          repFirms={repFirms}
          users={users}
          ownershipRoleTypes={ownershipRoleTypes}
          addOwner={addOwner}
          updateOwner={updateOwner}
          removeOwner={removeOwner}
          industryItems={industryItems}
          industrySearchText={industrySearchText}
          setIndustrySearchText={setIndustrySearchText}
          allAccounts={allAccounts}
          handleSetParent={handleSetParent}
        />
      )}
      {activeTab === 'overview' && (
        <AccountWeeklySummary accountId={id} canEdit={canDo('accounts', 'edit')} />
      )}

      {activeTab === 'contacts' && isModuleVisible('contacts') && (
        <AccountContactsTab
          accountId={id}
          sortedContacts={sortedContacts}
          contactsSort={contactsSort}
          handleContactsSort={handleContactsSort}
          canDo={canDo}
          handleOpenContactModal={handleOpenContactModal}
          setContactToDelete={setContactToDelete}
          setShowDeleteContactConfirm={setShowDeleteContactConfirm}
          reloadAccount={loadData}
        />
      )}

      {activeTab === 'influence-map' && isModuleVisible('contacts') && (
        <AccountInfluenceMapTab
          account={account}
          canDo={canDo}
          reloadAccount={loadData}
          leadSources={leadSources}
          repFirms={repFirms}
          users={users}
        />
      )}

      {activeTab === 'account-map' && (
        <AccountHierarchyMapTab
          account={account}
          canDo={canDo}
          reloadAccount={loadData}
        />
      )}

      {activeTab === 'org-chart' && isModuleVisible('contacts') && (
        <AccountOrgChartTab
          account={account}
          canDo={canDo}
        />
      )}

      {activeTab === 'opportunities' && isModuleVisible('opportunities') && (
        <AccountOpportunitiesTab
          accountId={id}
          sortedOpportunities={sortedOpportunities}
          opportunitiesSort={opportunitiesSort}
          handleOpportunitiesSort={handleOpportunitiesSort}
          canDo={canDo}
          setShowOpportunityModal={setShowOpportunityModal}
        />
      )}

      {activeTab === 'activities' && isModuleVisible('activities') && (
        <AccountActivitiesTab account={account} />
      )}

      {activeTab === 'tasks' && isModuleVisible('tasks') && (
        <AccountTasksTab
          account={account}
          sortedTasks={sortedTasks}
          tasksSort={tasksSort}
          handleTasksSort={handleTasksSort}
          canDo={canDo}
          reloadAccount={loadData}
        />
      )}

      {activeTab === 'quotes' && isModuleVisible('quotes') && (
        <AccountQuotesTab
          accountId={id}
          sortedQuotes={sortedQuotes}
          quotesSort={quotesSort}
          handleQuotesSort={handleQuotesSort}
          canDo={canDo}
          setShowQuoteModal={setShowQuoteModal}
        />
      )}

      {activeTab === 'orders' && isModuleVisible('orders') && (
        <AccountOrdersTab
          accountId={id}
          sortedOrders={sortedOrders}
          ordersSort={ordersSort}
          handleOrdersSort={handleOrdersSort}
        />
      )}

      {/* Modals */}
      <EditAccountModal
        showEditModal={showEditModal}
        setShowEditModal={setShowEditModal}
        formData={formData}
        setFormData={setFormData}
        handleUpdate={handleUpdate}
        stages={stages}
        leadSources={leadSources}
        users={users}
        ownershipRoleTypes={ownershipRoleTypes}
        repFirms={repFirms}
        addOwner={addOwner}
        updateOwner={updateOwner}
        removeOwner={removeOwner}
      />

      <LogActivityModal
        showActivityModal={showActivityModal}
        setShowActivityModal={(v) => { setShowActivityModal(v); if (!v) setModalError(''); }}
        activityForm={activityForm}
        setActivityForm={setActivityForm}
        handleLogActivity={handleLogActivity}
        contacts={account.contacts}
        error={modalError}
      />

      <CreateTaskModal
        showTaskModal={showTaskModal}
        setShowTaskModal={(v) => { setShowTaskModal(v); if (!v) setModalError(''); }}
        taskForm={taskForm}
        setTaskForm={setTaskForm}
        handleCreateTask={handleCreateTask}
        users={users}
        contacts={account.contacts}
        error={modalError}
      />

      <AddressModal
        showAddressModal={showAddressModal}
        setShowAddressModal={(v) => { setShowAddressModal(v); if (!v) setModalError(''); }}
        editingAddress={editingAddress}
        addressForm={addressForm}
        setAddressForm={setAddressForm}
        handleSaveAddress={handleSaveAddress}
        error={modalError}
      />

      <DeleteAccountModal
        showDeleteConfirm={showDeleteConfirm}
        setShowDeleteConfirm={setShowDeleteConfirm}
        account={account}
        deleteContacts={deleteContacts}
        setDeleteContacts={setDeleteContacts}
        handleDelete={handleDelete}
      />

      <ContactModal
        showContactModal={showContactModal}
        setShowContactModal={(v) => { setShowContactModal(v); if (!v) setModalError(''); }}
        editingContact={editingContact}
        setEditingContact={setEditingContact}
        contactForm={contactForm}
        setContactForm={setContactForm}
        handleSaveContact={handleSaveContact}
        leadSources={leadSources}
        repFirms={repFirms}
        users={users}
        accounts={contactAccountOptions}
        error={modalError}
      />

      <DeleteContactConfirm
        showDeleteContactConfirm={showDeleteContactConfirm}
        setShowDeleteContactConfirm={setShowDeleteContactConfirm}
        contactToDelete={contactToDelete}
        setContactToDelete={setContactToDelete}
        handleDeleteContact={handleDeleteContact}
      />

      <DeleteAddressConfirm
        showDeleteAddressConfirm={showDeleteAddressConfirm}
        setShowDeleteAddressConfirm={setShowDeleteAddressConfirm}
        setAddressToDelete={setAddressToDelete}
        confirmDeleteAddress={confirmDeleteAddress}
      />

      <CreateOpportunityModal
        showOpportunityModal={showOpportunityModal}
        setShowOpportunityModal={(v) => { setShowOpportunityModal(v); if (!v) setModalError(''); }}
        opportunityForm={opportunityForm}
        setOpportunityForm={setOpportunityForm}
        handleCreateOpportunity={handleCreateOpportunity}
        contacts={account.contacts}
        users={users}
        opportunityStages={opportunityStages}
        repFirms={repFirms}
        products={products}
        addOpportunityLineItem={addOpportunityLineItem}
        updateOpportunityLineItem={updateOpportunityLineItem}
        removeOpportunityLineItem={removeOpportunityLineItem}
        calculateOpportunityTotal={calculateOpportunityTotal}
        error={modalError}
      />

      <CreateQuoteModal
        showQuoteModal={showQuoteModal}
        setShowQuoteModal={(v) => { setShowQuoteModal(v); if (!v) setModalError(''); }}
        quoteForm={quoteForm}
        setQuoteForm={setQuoteForm}
        handleCreateQuote={handleCreateQuote}
        contacts={account.contacts}
        addresses={account.addresses}
        products={products}
        addQuoteLineItem={addQuoteLineItem}
        updateQuoteLineItem={updateQuoteLineItem}
        removeQuoteLineItem={removeQuoteLineItem}
        calculateQuoteTotal={calculateQuoteTotal}
        error={modalError}
      />


    </div>
  );
}

export default AccountDetail;
